import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Button, Card, ErrorBanner, Spinner } from '../components/ui'
import { beginLogin, completeLogin } from '../lib/auth'
import { hasCognito } from '../lib/config'
import { useBootstrap } from '../lib/queries'
import { useSession } from '../session'

/**
 * Sign in with Google (requirement 1).
 *
 * Doubles as the OAuth redirect target: the Hosted UI comes back to `/login?code=…`,
 * so the exchange happens here and the user never sees a separate callback screen
 * flash past.
 */
export function LoginPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { session, refresh } = useSession()
  const bootstrap = useBootstrap()
  const [error, setError] = useState<unknown>()
  const [exchanging, setExchanging] = useState(() => new URLSearchParams(location.search).has('code'))

  const returnTo = (location.state as { from?: string } | null)?.from ?? '/'

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const result = await completeLogin(location.search)
        if (!result || cancelled) {
          setExchanging(false)
          return
        }
        refresh()
        // Bootstrap is what creates the profile and redeems any invite sent to
        // this address before the account existed (design SS5.5), so it has to
        // run before the app makes its first authenticated request.
        await bootstrap.mutateAsync({})
        if (!cancelled) navigate(result.returnTo, { replace: true })
      } catch (err) {
        if (!cancelled) {
          setError(err)
          setExchanging(false)
        }
      }
    })()
    return () => {
      cancelled = true
    }
    // Runs once for the URL this page was opened with; re-running on every
    // `bootstrap` identity change would re-exchange a spent code.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // A session that already exists (a dev impersonation, or a revisit with a live
  // token) has nothing to do here.
  useEffect(() => {
    if (session && !exchanging) navigate(returnTo, { replace: true })
  }, [session, exchanging, navigate, returnTo])

  return (
    <div className="mx-auto flex min-h-full max-w-md flex-col justify-center px-6">
      <div className="mb-10 text-center">
        <div className="mx-auto mb-4 flex size-16 items-center justify-center rounded-2xl bg-ink-soft ring-1 ring-ink-line">
          <span className="text-3xl font-bold text-accent">V</span>
        </div>
        <h1 className="text-2xl font-semibold">VMoney</h1>
        <p className="mt-1 text-sm text-muted">兩個人的記帳與分帳</p>
      </div>

      <Card className="space-y-4 p-5">
        {exchanging ? (
          <Spinner label="正在完成登入" />
        ) : (
          <>
            <ErrorBanner error={error} />
            {hasCognito() ? (
              <Button
                className="w-full"
                onClick={() => {
                  setError(undefined)
                  beginLogin(returnTo).catch(setError)
                }}
              >
                使用 Google 登入
              </Button>
            ) : (
              <p className="text-sm text-muted">
                尚未設定 Cognito。本機開發請設定 <code className="text-paper">VITE_DEV_USER_ID</code>
                ，或設定 <code className="text-paper">VITE_COGNITO_DOMAIN</code> 與{' '}
                <code className="text-paper">VITE_COGNITO_CLIENT_ID</code> 後重新啟動。
              </p>
            )}
            <p className="text-center text-xs text-muted">
              只使用 Google 登入，本站不保存任何密碼。
            </p>
          </>
        )}
      </Card>
    </div>
  )
}
